// BUILD-046 Owner-requested layout revision (visual edits ledger, Layout-4): the split Done button of a My Tasks row.
//
//   [ ✓ Done | ▾ ]
// DONE is exactly what it was: a labelled solid green button, ONE click, ONE request, no body (doneFlow.performDone). Its saving state and its failure message are
// unchanged. The adjacent caret is "Done with note": a joined green button (aria-haspopup="dialog", aria-expanded, accessible name "Done with note: <title>", tooltip
// "Complete this task and record a note") that opens the EXISTING compact note panel (DoneNotePanel, drawn below the row by the row); the note, the activity date and the
// outcomes are untouched. A source-governed task shows neither: only the existing "completes through its source" text, which DoneWithNoteButton draws.
import type { DoneControlView } from './doneModel';
import { DoneWithNoteButton } from './DoneWithNote';
import type { DoneNoteControls } from './DoneWithNote';

export interface DoneSplitProps {
  view: DoneControlView;
  rowId: string;
  title: string;
  onDone: () => void;
  /** The Done-with-note controls; null on the legacy page, which has no such route (Done then stands alone). */
  note: DoneNoteControls | null;
  compact: boolean;
}

export function DoneSplit({ view, rowId, title, onDone, note, compact }: DoneSplitProps) {
  const caret = note !== null && note.offer.offered;
  const governed = note !== null && !note.offer.offered;
  if (!view.visible && view.failure === null && !governed) return null;
  return (
    <>
      {(view.visible || caret) && (
        <span className="inline-flex" data-testid="done-split">
          {view.visible && (
            <button
              type="button"
              disabled={view.disabled}
              onClick={onDone}
              aria-label={`Done: ${title}`}
              className={`${compact ? 'px-2.5 py-1' : 'px-3 py-1.5'} inline-flex items-center gap-1.5 border border-green-800 bg-green-700 text-sm font-semibold text-white hover:bg-green-800 disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-green-700 ${
                caret ? 'rounded-l-md' : 'rounded-md'
              }`}
            >
              <svg aria-hidden="true" viewBox="0 0 20 20" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4.5 10.5l3.5 3.5 7.5-8" />
              </svg>
              {view.label}
            </button>
          )}
          {caret && note !== null && <DoneWithNoteButton rowId={rowId} title={title} controls={note} compact={compact} variant="caret" />}
        </span>
      )}
      {governed && note !== null && <DoneWithNoteButton rowId={rowId} title={title} controls={note} compact={compact} />}
      {view.failure !== null && (
        <span role="alert" className="text-xs text-red-700">
          {view.failure.headline}
          {view.failure.detail !== null && <span className="ml-1">{view.failure.detail}</span>}
        </span>
      )}
    </>
  );
}
